import type { FocusEvent } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { env } from '@/shared/lib/env';
import { Input } from '@/shared/ui/inputs/Input';
import { Section } from '@/shared/ui/layout/Section';
import type { AdminParcelFormValues } from '@/features/parcel-form/modules/types';

type Props = {
  form: UseFormReturn<AdminParcelFormValues>;
  // Optional side-effects the parcel-create flow uses to keep the
  // "Joʻnatishga tasdiqlangan" checkbox in sync with the trek_id input.
  // ``onTrekIdBlur`` fires after RHF's own blur handler so the warehouse
  // probe can run; ``onTrekIdChange`` fires on every keystroke so a stale
  // checkbox tick can be reset before it ever submits.
  onTrekIdBlur?: (value: string) => void;
  onTrekIdChange?: (value: string) => void;
};

export const FeaturesParcelFormSectionBasic = ({
  form,
  onTrekIdBlur,
  onTrekIdChange,
}: Props) => {
  const { t } = useTranslation();
  const { register, formState, setValue } = form;
  const errors = formState.errors;
  const translateError = (msg?: string) =>
    msg ? t(msg, { length: env.pinflLength }) : undefined;
  // Register trek_id with ``setValueAs`` so any value RHF reads (paste,
  // programmatic ``setValue`` callers that forget to upper-case, etc.) is
  // normalised. The custom ``onChange`` below additionally mutates the
  // DOM input so the operator visibly types in upper-case in real time.
  const trekIdReg = register('trek_id', {
    setValueAs: (v: unknown) => (typeof v === 'string' ? v.toUpperCase() : ''),
  });
  return (
    <Section title={t('parcels.form.sections.basic')}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          label={t('parcels.form.fields.name')}
          required
          error={translateError(errors.name?.message)}
          {...register('name')}
        />
        <Input
          label={t('parcels.form.fields.trekId')}
          required
          autoCapitalize="characters"
          maxLength={255}
          error={translateError(errors.trek_id?.message)}
          {...trekIdReg}
          onChange={(e) => {
            const upper = e.target.value.toUpperCase();
            if (e.target.value !== upper) e.target.value = upper;
            setValue('trek_id', upper, { shouldValidate: true, shouldDirty: true });
            onTrekIdChange?.(upper);
          }}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            const upper = e.target.value.toUpperCase();
            if (e.target.value !== upper) e.target.value = upper;
            void trekIdReg.onBlur(e);
            onTrekIdBlur?.(upper);
          }}
        />
      </div>
      <Input
        label={t('parcels.form.fields.storeName')}
        error={translateError(errors.store_name?.message)}
        {...register('store_name')}
      />
    </Section>
  );
};
